import { NetworkId } from '@kwenta/sdk/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useAccountAbstractionConnect from 'services/biconomy/useAccountAbstractionConnect'
import { createContainer } from 'unstated-next'
import { useAccount, useNetwork, useProvider, useSigner } from 'wagmi'

import { SUPPORTED_NETWORKS } from 'constants/network'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import sdk from 'state/sdk'
import { setSigner } from 'state/wallet/actions'
import { setProviderNetwork, setSignerNetworkId } from 'state/wallet/reducer'
import { selectIsWatcherMode, selectWallet } from 'state/wallet/selectors'

import { generateExplorerFunctions, getBaseUrl } from './blockExplorer'
import { chain } from './config'

export let blockExplorer = generateExplorerFunctions(getBaseUrl(10))

const useConnector = () => {
	const dispatch = useAppDispatch()
	const { chain: networkChain } = useNetwork()
	const { isConnected: isAccountWalletConnected } = useAccount({
		onDisconnect: () => dispatch(setSigner(null)),
	})

	const [providerReady, setProviderReady] = useState(false)

	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const watcherWallet = useAppSelector(selectWallet)
	const isWalletConnected = isAccountWalletConnected || (isWatcherMode && !!watcherWallet)
	const activeChain = isWatcherMode ? chain.optimism : networkChain

	const network = useMemo(() => {
		return SUPPORTED_NETWORKS.includes(activeChain?.id ?? chain.optimism.id)
			? activeChain ?? chain.optimism
			: chain.optimism
	}, [activeChain])

	const provider = useProvider({ chainId: network.id })
	const l2Provider = useProvider({ chainId: chain.optimism.id })
	const { data: signer } = useSigner()

	useAccountAbstractionConnect({ signer })

	const handleNetworkChange = useCallback(
		(networkId: NetworkId) => {
			dispatch(setProviderNetwork(networkId))
			blockExplorer = generateExplorerFunctions(getBaseUrl(networkId))
		},
		[dispatch]
	)

	useEffect(() => {
		dispatch(setSignerNetworkId(activeChain?.id))
	}, [activeChain?.id, dispatch])

	useEffect(() => {
		if (!!provider) {
			sdk.setProvider(provider).then((networkId) => {
				handleNetworkChange(networkId)
				setProviderReady(true)
			})
		}
	}, [provider, handleNetworkChange])

	useEffect(() => {
		if (!isWatcherMode) {
			dispatch(setSigner(signer))
		}
	}, [signer, dispatch, isWatcherMode])

	return {
		activeChain,
		isWalletConnected,
		provider,
		l2Provider,
		signer,
		network,
		providerReady,
	}
}

const Connector = createContainer(useConnector)

export default Connector
